if (!customElements.get('gz-product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this.product = JSON.parse(this.getAttribute('product')) || null;
      this.msrp = this.getAttribute('msrp') || null;
    }

    connectedCallback() {
      this.renderProductCard();
    }

    static get observedAttributes() {
      return ['product', 'msrp'];
    }

    attributeChangedCallback() {
      this.renderProductCard();
    }

    getUniqueColorOptions(product) {
      const uniqueColorOptions = new Set();
      for (let i = 0; i < product.variants.length; i++) {
        const variant = product.variants[i];
        const firstOption = variant.option1;
        firstOption && uniqueColorOptions.add(firstOption);
      }
      return Array.from(uniqueColorOptions);
    }

    toggleProductCardStyling() {
      const priceElements = this.querySelectorAll('.gz-primary-pricing, .gz-sale-clr');
      const headingElement = this.querySelector('.card__heading');
      const swatchElement = this.querySelector('.gz-color-swatches');
      const saleBadgeElement = this.querySelector('.gz-sale-badge');

      priceElements.forEach(element => element.classList.toggle('no-styles'));

      if (headingElement) {
        headingElement.classList.toggle('no-styles', priceElements[0].classList.contains('no-styles'));
      }

      priceElements.forEach(element => {
        element.textContent =
          element.classList.contains('gz-primary-pricing') && element.classList.contains('no-styles')
            ? `From ${element.textContent.replace(/^From\s/, '')}`
            : element.textContent.replace(/^From\s/, '');
      });

      if (swatchElement) swatchElement.classList.toggle('hidden');

      if (saleBadgeElement) {
        saleBadgeElement.classList.toggle('gz-sale-badge');
      }
    }

    formatPrice(price) {
      return price > 0 ? (price / 100).toFixed(2) : '';
    }

    renderProductCard() {
      const product = this.product;
      if (!product) return;

      const mediaAspectRatio = 'square';
      const imageShape = 'default';
      const showVendor = false;
      const ratio = mediaAspectRatio === 'portrait' ? 0.8 : 1;
      const isSale = product.price_min < product.compare_at_price;
      const featuredMedia = product.featured_image || product.media[0]?.src || product.media[0]?.preview_image_original;
      const altText = product.media[0]?.alt || product.title;
      let uniqueColorOptions = this.getUniqueColorOptions(product);

      // Maximum 8 swatches
      if (uniqueColorOptions.length > 8) {
        uniqueColorOptions = uniqueColorOptions.slice(0, 8);
      }

      const imageSrcset = featuredMedia
        ? `
        ${featuredMedia}?width=165 165w,
        ${featuredMedia}?width=360 360w,
        ${featuredMedia}?width=533 533w,
        ${featuredMedia}?width=720 720w,
        ${featuredMedia}?width=940 940w,
        ${featuredMedia}?width=1066 1066w
      `
        : '';

      const sizes = `(min-width: 1200px) calc((100vw - 130px) / 4), 
                     (min-width: 990px) calc((100vw - 130px) / 4), 
                     (min-width: 750px) calc((100vw - 120px) / 3), 
                     calc((100vw - 35px) / 2)`;

      const imageHtml = featuredMedia
        ? `
          <div class="card__media${imageShape !== 'default' ? ` shape--${imageShape}` : ''}">
            <div class="media media--transparent media--hover-effect">
              <img
                srcset="${imageSrcset}"
                src="${featuredMedia}?width=533"
                sizes="${sizes}"
                alt="${altText}"
                class="motion-reduce"
                loading="lazy"
                width="500"
                height="500"
              >
              ${this.renderSecondaryImage(product)}
            </div>
          </div>`
        : '';

      const vendorHtml = showVendor ? `<div class="caption-with-letter-spacing light">${product.vendor}</div>` : '';

      const swatchesHtml = uniqueColorOptions
        .map(color => {
          const swatchStyle = this.getSwatchStyle(color);
          return `
            <span class="gz-color-swatch" title="${color}" style="${swatchStyle}"></span>
          `;
        })
        .join('');
      const MsrpOrCompareAt = isSale ? this.formatPrice(product.compare_at_price) : this.formatPrice(this.msrp);
      const PriceMinimum = this.formatPrice(product.price_min);
      const msrpDisplay = MsrpOrCompareAt
        ? `<span class="price-item price-item--regular">$${MsrpOrCompareAt}${!isSale ? ' new' : ''}</span>`
        : '';

      const priceHtml = product.compare_at_price
        ? `<div  class="price price--on-sale">
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular ${isSale ? 'gz-sale-clr' : ''}"> $${PriceMinimum}</span>
            </div>
            <div style="display: flex !important; justify-content: center; align-items: center;" class="price__sale">
              <span>
              ${msrpDisplay}
              </span>
              <span  class="gz-primary-pricing ${isSale ? 'gz-sale-clr' : ''}">$${PriceMinimum}</span>
            </div>
          </div>
        </div>`
        : `<div  class="price price--on-sale">
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular ${isSale ? 'gz-sale-clr' : ''}"> $${PriceMinimum}</span>
            </div>
            <div style="display: flex !important; justify-content: center; align-items: center;" class="price__sale">
              <span>
                ${msrpDisplay}
              </span>
              <span  class="gz-primary-pricing ${isSale ? 'gz-sale-clr' : ''}">$${PriceMinimum}</span>
            </div>
          </div>
        </div>`;

      // Combine everything into the final HTML for the product card
      this.innerHTML = `
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--${mediaAspectRatio} ${
        imageShape !== 'default' ? `card--shape shape--${imageShape}` : ''
      } ${featuredMedia ? 'card--media' : ''}" style="--ratio-percent: ${(1 / ratio) * 100}%">
            <div class="card__inner ratio" style="--ratio-percent: ${(1 / ratio) * 100}%">
              ${imageHtml}
              <div class="card__content">
              ${
                !product.available
                  ? '<div class="card__badge bottom left"><span  class="badge badge--bottom-left color-scheme-6200b117-b5fe-4032-a2bd-5582a9db9128">Sold out</span></div>'
                  : ''
              }
                 ${
                   isSale && product.available
                     ? '<div class="card__badge bottom left"><span  class="gz-sale-badge badge badge--bottom-left color-scheme-9e504077-bb4e-40ee-975c-3b92b42aa483">Sale</span></div>'
                     : ''
                 }
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="/products/${product.handle}" class="full-unstyled-link">${product.title}</a>
                </h3>
                <div class="gz-color-swatches">${swatchesHtml}</div>
                ${vendorHtml}
                ${priceHtml}
              </div>
            </div>
          </div>
        </div>
      `;
    }

    renderSecondaryImage(product) {
      let showSecondaryImage = true;
      let secondaryMedia = product.media[1]?.preview_image_original || product.media[0]?.preview_image_origina || null

      if (secondaryMedia && showSecondaryImage) {
        const secondarySrcset = `
        ${secondaryMedia}?width=165 165w,
        ${secondaryMedia}?width=360 360w,
        ${secondaryMedia}?width=533 533w,
        ${secondaryMedia}?width=720 720w,
        ${secondaryMedia}?width=940 940w,
        ${secondaryMedia}?width=1066 1066w
      `;

        return `
        <img
          srcset="${secondarySrcset}"
          src="${secondaryMedia}?width=533"
          sizes="(min-width: 1200px) calc((100vw - 130px) / 4), (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
          alt=""
          class="motion-reduce"
          loading="lazy"
          width="500"
          height="500"
        >
      `;
      }
      return '';
    }

    getSwatchStyle(color) {
      const swatch = window.GzTheme?.SWATCHES.find(
        s => s.name.toLowerCase() === color.toLowerCase().replace(/\s+/g, '-'),
      );
      return swatch ? `background-color: ${swatch.color};` : 'background-color: #CCCCCC;';
    }
  }
  customElements.define('gz-product-card', ProductCard);
}
